import { useEffect, useRef, useState } from "react";
import { UploadCloud, Download, AlertTriangle, Info, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, download } from "../../lib/api";
import { StatusBadge } from "../StatusBadge";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";

const ICON = { error: XCircle, warning: AlertTriangle, info: Info };
const COLOR = { error: "text-rose-400", warning: "text-amber-400", info: "text-blue-400" };

function Warnings({ list }) {
  if (!list?.length) return <p className="text-sm text-emerald-400 flex items-center gap-2"><CheckCircle2 size={16} />No extraction warnings</p>;
  return (
    <ul className="space-y-2" data-testid="extraction-warnings">
      {list.map((w, i) => { const I = ICON[w.level] || Info; return <li key={i} className={`text-sm flex gap-2 ${COLOR[w.level]}`}><I size={16} className="shrink-0 mt-0.5" /><span className="text-slate-300">{w.message}</span></li>; })}
    </ul>
  );
}

function Preview({ up, onImported }) {
  const [ack, setAck] = useState(false);
  const ex = up.extraction;
  const hasErr = ex?.warnings?.some((w) => w.level === "error");
  const doImport = async () => {
    try {
      const { data } = await api.post(`/uploads/${up.id}/import`, { acknowledge_warnings: ack });
      toast.success(`Imported as version ${data.number} (verified)`);
      onImported();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-6 fade-up" data-testid={`extraction-preview-${up.id}`}>
      <div className="flex flex-wrap justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow mb-1">Extraction preview</p>
          <p className="font-display text-xl">{up.filename}</p>
          <p className="text-sm text-slate-400 mt-1" data-testid="extraction-summary">{ex.sections.length} sections · {ex.chapter_count} chapters · ~{ex.word_count.toLocaleString()} words</p>
        </div>
        {up.imported_version_id ? <StatusBadge status="applied" label="Imported" /> : (
          <div className="flex flex-col items-end gap-2">
            {hasErr && <label className="text-xs text-rose-300 flex gap-2 items-center"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="import-acknowledge" />I reviewed the errors; import anyway</label>}
            <Button onClick={doImport} disabled={!ex.sections.length || (hasErr && !ack)} className="bg-amber-600 hover:bg-amber-500" data-testid="import-manuscript-button">Import as new version</Button>
          </div>
        )}
      </div>
      <Warnings list={ex.warnings} />
      <div className="mt-6 border border-slate-800 rounded-lg divide-y divide-slate-800 max-h-[420px] overflow-y-auto scrollbar-thin">
        {ex.sections.map((s, i) => (
          <div key={s.id} className="p-4" data-testid={`extracted-section-${i}`}>
            <div className="flex justify-between gap-3 text-sm"><span className="text-slate-100">{i + 1}. {s.title}</span><span className="flex gap-2 items-center"><StatusBadge status="pending" label={s.kind.replace("_", " ")} /><span className="font-mono text-xs text-slate-500">{s.word_count} w</span></span></div>
            {s.preview && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{s.preview}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UploadTab({ book, reload }) {
  const [uploads, setUploads] = useState([]);
  const [kind, setKind] = useState("manuscript");
  const [progress, setProgress] = useState(null);
  const [selected, setSelected] = useState(null);
  const input = useRef();
  const load = () => api.get(`/books/${book.id}/uploads`).then((r) => setUploads(r.data));
  useEffect(() => { load(); }, [book.id]); // eslint-disable-line

  const onFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind);
    setProgress(0);
    try {
      const { data } = await api.post(`/books/${book.id}/uploads`, fd, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 90)) });
      setProgress(100);
      toast.success(kind === "manuscript" ? "Uploaded and extracted. Original stored immutably." : "Uploaded and stored immutably.");
      await load();
      setSelected(data.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setTimeout(() => setProgress(null), 800); if (input.current) input.current.value = ""; }
  };
  const sel = uploads.find((u) => u.id === selected);
  return (
    <div className="space-y-6">
      <div className="panel p-8 border-dashed" data-testid="upload-zone"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}>
        <div className="flex flex-wrap items-center gap-6">
          <UploadCloud className="text-amber-500" size={36} />
          <div className="flex-1 min-w-[220px]">
            <p className="font-display text-xl">Upload a file</p>
            <p className="text-xs text-slate-400 mt-1">Manuscripts: DOCX, EPUB, PDF, RTF, TXT, MD · Artwork: PNG, JPEG, PDF · Audio: MP3, WAV, M4A · Max 60 MB. Legacy .DOC must be re-saved as DOCX.</p>
          </div>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm" data-testid="upload-kind-select">
            <option value="manuscript">Manuscript</option><option value="artwork">Cover artwork</option><option value="audio">Narration audio</option>
          </select>
          <input ref={input} type="file" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="upload-file-input" />
          <Button onClick={() => input.current.click()} className="bg-amber-600 hover:bg-amber-500" data-testid="upload-choose-button">Choose file</Button>
        </div>
        {progress !== null && <div className="mt-6"><Progress value={progress} className="h-2" data-testid="upload-progress" /><p className="text-xs text-slate-500 mt-2">{progress < 90 ? "Uploading…" : progress < 100 ? "Extracting & verifying…" : "Done"}</p></div>}
      </div>
      {sel?.extraction && <Preview up={sel} onImported={() => { load(); reload(); }} />}
      <div className="panel" data-testid="uploads-list">
        <p className="eyebrow p-5 pb-0">Original uploads (immutable)</p>
        {uploads.length === 0 && <p className="p-5 text-sm text-slate-500">Nothing uploaded yet.</p>}
        <div className="divide-y divide-slate-800 mt-3">
          {uploads.map((u) => (
            <div key={u.id} className="p-4 px-5 flex flex-wrap items-center gap-3 justify-between" data-testid={`upload-row-${u.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-slate-200 truncate">{u.filename}</p>
                <p className="text-xs text-slate-500 font-mono">{u.kind} · {(u.size / 1024).toFixed(1)} KB · sha256 {u.sha256.slice(0, 10)}… · {fmtDate(u.created_at)}</p>
              </div>
              <div className="flex gap-2 items-center">
                <StatusBadge status={u.imported_version_id ? "applied" : u.status} label={u.imported_version_id ? "imported" : undefined} />
                {u.kind === "manuscript" && !u.imported_version_id && u.extraction?.sections?.length > 0 && <Button size="sm" onClick={() => setSelected(u.id)} className="bg-amber-600 hover:bg-amber-500" data-testid={`upload-import-${u.id}`}>Import to use in tools</Button>}
                {u.extraction && <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setSelected(u.id)} data-testid={`upload-preview-${u.id}`}>Preview</Button>}
                <Button size="sm" variant="outline" className="border-slate-700" onClick={() => download(`/uploads/${u.id}/download`, u.filename).catch((e) => toast.error(errMsg(e)))} data-testid={`upload-download-${u.id}`}><Download size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
